/**
 * WorkflowRepository — data access for ExposedWorkflow entities.
 *
 * Follows FERP repository pattern: constructor DI of ApolloClient.
 * Encapsulates form schema fetching (the core portal use case).
 * UI components never import this directly — use hooks instead.
 */
import { ApolloClient, gql } from '@apollo/client/core';
import type { ExposedWorkflow } from '../types';
import { apolloClient } from '../../client';

const FORM_FIELD_FRAGMENT = gql`
  fragment FormFieldFields on GqlFormField {
    id
    label
    fieldType
    required
    placeholder
    minLength
    maxLength
    pattern
    options {
      value
      label
    }
    conditionalFieldId
    conditionalEquals
  }
`;

const FORM_STEP_FRAGMENT = gql`
  fragment FormStepFields on GqlFormStep {
    title
    description
    fields {
      ...FormFieldFields
    }
  }
  ${FORM_FIELD_FRAGMENT}
`;

const GET_EXPOSED_WORKFLOWS = gql`
  query GetExposedWorkflows($portalId: ID!) {
    exposedWorkflows(portalId: $portalId) {
      id
      slug
      displayName
      workflowName
      workflowVersion
      access
      captchaRequired
      status
      steps {
        ...FormStepFields
      }
      confirmationType
      confirmationContent
    }
  }
  ${FORM_STEP_FRAGMENT}
`;

export class WorkflowRepository {
  constructor(private readonly client: ApolloClient) {}

  async getByPortalId(portalId: string): Promise<ExposedWorkflow[]> {
    const { data } = await this.client.query<{ exposedWorkflows: ExposedWorkflow[] }>({
      query: GET_EXPOSED_WORKFLOWS,
      variables: { portalId },
    });
    return data?.exposedWorkflows ?? [];
  }

  async getBySlug(portalId: string, workflowSlug: string): Promise<ExposedWorkflow | null> {
    const workflows = await this.getByPortalId(portalId);
    return workflows.find((w) => w.slug === workflowSlug) ?? null;
  }
}

export const workflowRepository = new WorkflowRepository(apolloClient);
